'use client'

import React from "react";
import {cn} from "@/lib/utils";
import Tasks from "@/app/components/icons/Tasks.svg"
import PRD from "@/app/components/icons/PRD.svg"
import Development from "@/app/components/icons/Development.svg"
import Bot from "@/app/components/icons/Bot.svg"
import Linear from "@/app/components/icons/linear.svg"
import Jira from "@/app/components/icons/jira.svg"
import Slack from "@/app/components/icons/Slack.svg"
import Teams from "@/app/components/icons/Teams.svg"
import Zoom from "@/app/components/icons/Zoom.svg"
import Github from "@/app/components/icons/github-mark.svg"
import DevplanWhite from "@/app/components/icons/DevplanWhite.svg"
import Devplan from "@/app/components/icons/Devplan.svg"
import DevplanClosed from "@/app/components/icons/Logo-Closed.svg"
import Google from "@/app/components/icons/google.svg"
import GaugeLow from "@/app/components/icons/gauge_low.svg"
import GaugeMedium from "@/app/components/icons/gauge_medium.svg"
import GaugeHigh from "@/app/components/icons/gauge_high.svg"
import BitBucket from "@/app/components/icons/bitbucket.svg"
import Git from "@/app/components/icons/git.svg"
import GitLab from "@/app/components/icons/gitlab.svg"
import Cursor from "@/app/components/icons/cursor.svg"
import Claude from "@/app/components/icons/claude.svg"
import Junie from "@/app/components/icons/junie.svg"
import OpenAI from "@/app/components/icons/openai.svg"
import PyCharm from "@/app/components/icons/pycharm.svg"
import IntelliJ from "@/app/components/icons/intellij.svg"
import WebStorm from "@/app/components/icons/webstorm.svg"
import GoLand from "@/app/components/icons/goland.svg"
import GoogleDrive from "@/app/components/icons/google-drive.svg"
import Granola from "@/app/components/icons/granola.svg"
import Notion from "@/app/components/icons/notion.svg"
import Confluence from "@/app/components/icons/confluence.svg"
import {Award, CodeXml, CopyCheck, ListChecks, Palette} from "lucide-react";

type IconName = 'PRD' | 'Tasks' | 'Development' | 'Bot' | 'Linear' | 'DevplanWhite' | 'Devplan' | 'Google' | 'Github'
  | 'DevplanClosed' | 'GaugeLow' | 'GaugeMedium' | 'GaugeHigh' | 'Jira' | 'BitBucket' | 'Git' | 'Slack' | 'Teams'
  | 'GitLab' | 'Cursor' | 'Claude' | 'Junie' | 'OpenAI' | 'PyCharm' | 'IntelliJ' | 'WebStorm' | 'GoLand' | 'GoogleDrive' | 'Granola' | 'Notion' | 'Confluence' | 'Zoom';

const icons: Record<IconName, React.FC<React.SVGProps<SVGSVGElement>>> = {
  'PRD': PRD,
  'Tasks': Tasks,
  'Development': Development,
  'Bot': Bot,
  'Linear': Linear,
  'DevplanWhite': DevplanWhite,
  'Devplan': Devplan,
  'DevplanClosed': DevplanClosed,
  'Google': Google,
  'Github': Github,
  'GaugeLow': GaugeLow,
  'GaugeMedium': GaugeMedium,
  'GaugeHigh': GaugeHigh,
  'Jira': Jira,
  'BitBucket': BitBucket,
  'Git': Git,
  'GitLab': GitLab,
  'Slack': Slack,
  'Teams': Teams,
  'Cursor': Cursor,
  'Claude': Claude,
  'Junie': Junie,
  'OpenAI': OpenAI,
  'PyCharm': PyCharm,
  'IntelliJ': IntelliJ,
  'WebStorm': WebStorm,
  'GoLand': GoLand,
  'GoogleDrive': GoogleDrive,
  'Granola': Granola,
  'Notion': Notion,
  'Confluence': Confluence,
  'Zoom': Zoom
};

function DevplanIcon(
  {iconName, className, viewBox}: { iconName: IconName, className?: string, viewBox?: string }
) {
  const Icon = icons[iconName];
  return <span className={cn("inline-block w-6 h-6", className)}>
    <svg
      viewBox={viewBox ?? "0 0 24 23"}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{width: '100%', height: '100%'}}
    >
      <Icon/>
    </svg>
  </span>
}

export function IconPrd({className}: { className?: string }) {
  return <DevplanIcon iconName="PRD" className={className}/>
}

export function IconTasks({className}: { className?: string }) {
  return <DocumentIcon Icon={CopyCheck} className={cn("bg-slate-500", className)}/>
}

export function IconDevelopment({className}: { className?: string }) {
  return <DocumentIcon Icon={CodeXml} className={cn("bg-sky-500", className)}/>
}

export function IconTest({className}: { className?: string }) {
  return <DocumentIcon Icon={ListChecks} className={cn("bg-amber-500", className)}/>
}

export function IconRelease({className}: { className?: string }) {
  return <DocumentIcon Icon={Award} className={cn("bg-emerald-500", className)}/>
}

export function IconDesign({className}: { className?: string }) {
  return <DocumentIcon Icon={Palette} className={cn("bg-violet-500", className)}/>
}

export function IconLinear({className}: { className?: string }) {
  return <DevplanIcon iconName="Linear" className={className} viewBox="0 0 200 200"/>
}

export function IconJira({className}: { className?: string }) {
  return <DevplanIcon iconName="Jira" className={className} viewBox="0 0 200 200"/>
}

export function IconBitBucket({className}: { className?: string }) {
  return <DevplanIcon iconName="BitBucket" className={className} viewBox="0 0 48 48"/>
}

export function IconGit({className}: { className?: string }) {
  return <RawDevplanIcon iconName="Git" className={className} viewBox="0 0 92 92"/>
}

export function IconGitLab({className}: { className?: string }) {
  return <RawDevplanIcon iconName="GitLab" className={className} viewBox="0 0 256 236"/>
}

export function IconSlack({className}: { className?: string }) {
  return <DevplanIcon iconName="Slack" className={className} viewBox="0 0 512 512"/>
}

export function IconGithub({className}: { className?: string }) {
  return <DevplanIcon iconName="Github" className={className} viewBox="0 0 98 96"/>
}

export function IconTeams({className}: { className?: string }) {
  return <DevplanIcon iconName="Teams" className={className} viewBox="0 0 48 48"/>
}

export function IconZoom({className}: { className?: string }) {
  return <DevplanIcon iconName="Zoom" className={className} viewBox="0 0 48 48"/>
}

export function IconDevplanWhite({className}: { className?: string }) {
  return <DevplanIcon iconName="DevplanWhite" className={className} viewBox="0 0 60 36"/>
}

export function IconDevplan({className}: { className?: string }) {
  return <DevplanIcon iconName="Devplan" className={className} viewBox="0 0 34 20"/>
}

export function IconDevplanClosed({className}: { className?: string }) {
  return <DevplanIcon iconName="DevplanClosed" className={className} viewBox="0 0 32 32"/>
}

export function IconGoogle({className}: { className?: string }) {
  return <DevplanIcon iconName="Google" className={className} viewBox="0 0 150 150"/>
}

export function IconGaugeLow({className}: { className?: string }) {
  return <DevplanIcon iconName="GaugeLow" className={className} viewBox="0 0 15 12"/>
}

export function IconGaugeMedium({className}: { className?: string }) {
  return <DevplanIcon iconName="GaugeMedium" className={className} viewBox="0 0 15 12"/>
}

export function IconGaugeHigh({className}: { className?: string }) {
  return <DevplanIcon iconName="GaugeHigh" className={className} viewBox="0 0 15 12"/>
}

export function IconCursor({className}: { className?: string }) {
  return <DevplanIcon iconName="Cursor" className={className} viewBox="0 0 466.73 532.09"/>
}

export function IconClaude({className}: { className?: string }) {
  return <DevplanIcon iconName="Claude" className={cn("text-[#C15F3C]", className)} viewBox="0 0 16 16"/>
}

export function IconJunie({className}: { className?: string }) {
  return <DevplanIcon iconName="Junie" className={className} viewBox="0 0 48 48"/>
}

export function IconOpenAI({className}: { className?: string }) {
  return <DevplanIcon iconName="OpenAI" className={className} viewBox="0 0 16 16"/>
}

export function IconPyCharm({className}: { className?: string }) {
  return <DevplanIcon iconName="PyCharm" className={className} viewBox="0 0 64 64"/>
}

export function IconIntelliJ({className}: { className?: string }) {
  return <DevplanIcon iconName="IntelliJ" className={className} viewBox="0 0 64 64"/>
}

export function IconWebStorm({className}: { className?: string }) {
  return <DevplanIcon iconName="WebStorm" className={className} viewBox="0 0 64 64"/>
}

export function IconGoLand({className}: { className?: string }) {
  return <DevplanIcon iconName="GoLand" className={className} viewBox="0 0 64 64"/>
}

export function IconGoogleDrive({className}: { className?: string }) {
  return <DevplanIcon iconName="GoogleDrive" className={className} viewBox="0 0 87.3 78"/>
}

export function IconGranola({className}: { className?: string }) {
  return <DevplanIcon iconName="Granola" className={className} viewBox="0 0 1308.2438965 1350"/>
}

export function IconNotion({className}: { className?: string }) {
  return <DevplanIcon iconName="Notion" className={className} viewBox="0 0 24 24"/>
}

export function IconConfluence({className}: { className?: string }) {
  return <DevplanIcon iconName="Confluence" className={className} viewBox="0 0 24 24"/>
}

function DocumentIcon({Icon, className}: { Icon: React.ElementType, className?: string }) {
  return <div className={cn("flex items-center justify-center h-[20px] w-[20px] rounded-md", className)}>
    <Icon className="h-3 w-3 text-white" strokeWidth={2.5}/>
  </div>
}

function RawDevplanIcon(
  {iconName, className, viewBox}: { iconName: IconName, className?: string, viewBox?: string }
) {
  const Icon = icons[iconName];
  // Render the SVG component directly to avoid clipping by an outer <svg>
  return <Icon className={cn("w-6 h-6", className)} viewBox={viewBox} />;
}
